import { Dialog, Toast } from 'antd-mobile';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { IWorksFolder } from '../typing';
import { useEffect, useState } from 'react';
import TouchMoveIcon from '@/images/h5-self-service/touch_move.webp';
import { transAgeToString } from '@/utils/TransData';
import { FolderImage } from '@/utils/imageDefaultUtils';
import { BaseInfo } from './BaseInfo';
import { BtnBox } from './BtnBox';
import { Loading } from '@/components/loading';
import { useSearchParams } from 'react-router-dom';
import { getDirectoryById, moveFolder, deleteFolder } from '@/restful/generate_book';
import EmptyBg from '@/images/photo_generate_book/empty_bg.webp';
import EmptyTips from '@/images/photo_generate_book/empty_tips.webp';
import VConsole from 'vconsole';
import ErrorBoundary from '@/components/ErrorBoundary';
import { openWebView } from '@/utils/jsUtils';
import { openAlbumList, openPictureBookDetailPage, openPictureBookPdfUploadPage, openPictureBookQuickInputPage, openUpdateListBottomSheet } from '@/utils/openNativePage';
import AddResourceMethodList from '@/components/upload/AddResourceMethodList';
import CreateFolderMethodList from '@/components/CreateFolderMethodList';
import { EFROM } from '@/typings/baiduPan';
import { getAppVersion, compareVersion } from '@/utils/EnvUtils';
import { JsBridge } from '@/utils/JsBridge';
import Header from '@/components/h5/Header';
import IconShare from '@/images/h5-common/icon_share.webp';
import { IMAGE_SHARE } from '@/images/h5-vip-share';

function ResourcesList() {
  const [detailData, setDetailData] = useState<IWorksFolder>(); // 当前页面的所有数据
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [showMethodList, setShowMethodList] = useState(false);
  const [showCreateMethodList, setShowCreateMethodList] = useState(false);

  // 检查是否支持百度网盘功能
  const shouldShowBaiduPan = () => {
    const currentVersion = getAppVersion();
    if (!currentVersion) return true; // 如果获取不到版本号，默认显示
    // 当前版本大于等于2.12.0时才显示百度网盘功能
    return compareVersion(currentVersion, '2.12.0');
  };

  // Fetch directory data by ID
  const fetchDirectoryData = (directoryId: number) => {
    return getDirectoryById(directoryId)
      .then((res) => {
        if (res.success && res.data) {
          const _data = transformDirectoryData(res.data);
          setDetailData(_data);
        } else {
          Toast.show({
            content: res.msg || '获取数据失败',
          });
        }
      })
      .catch((e) => {
        console.error('getDirectoryById error: ', e);
        Toast.show({
          content: e?.response?.data?.msg || '获取数据失败',
        });
      })
      .finally(() => {
      });
  };

  // Transform directory data to IWorksFolder format
  function transformDirectoryData(data: any): IWorksFolder {
    return {
      id: data.id,
      name: data.title,
      parentType: data.parentType,
      tagsGroup: {
        category: data.rootTitle && data.rootId ? {
          name: data.rootTitle,
          id: data.rootId,
        } : undefined,
        age: transAgeToString(data.ages),
        tags: data.tags || [],
      },
      description: data.intro,
      fileList: [],
      imageUrl: data.imageUrl,
      childCnt: data.childCnt,
      type: data.type,
      status: data.status,
      rootId: data.rootId,
      updateTime: data.updateTime,
      items: data.items?.map((item: any) => ({
        id: item.id,
        name: item.title,
        tagsGroup: {
          category: item.rootTitle && item.rootId ? {
            name: item.rootTitle,
            id: item.rootId,
          } : undefined,
          age: transAgeToString(item.ages),
          tags: item.tags || [],
        },
        description: item.intro,
        fileList: [],
        imageUrl: item.imageUrl,
        childCnt: item.childCnt,
        type: item.type,
        status: item.status,
        rootId: item.rootId,
        updateTime: item.updateTime,
      })),
    };
  }

  // 打开-编辑目录信息
  function handleOpenEdit() {
    openWebView({
      path: `/generate_book/create?id=${id}`,
      title: '编辑目录',
    })
  }

  // 创建二级目录
  function handleCreateWorksFolder() {
    setShowCreateMethodList(true);
  }

  // 手动创建目录
  function handleManualCreateFolder() {
    const parentId = detailData?.id;
    openWebView({
      path: `/generate_book/create?parentId=${parentId}`,
      title: '创建目录',
    })
  }

  // 百度网盘导入目录
  function handleBaiduPanImportFolder() {
    openWebView({
      path: `/self_service_baidu?from=${EFROM.FOLDER}&parentId=${detailData?.id}&supportSuffix=pdf`,
      title: '百度网盘导入',
    })
  }

  // 添加文件
  function handleAddFiles() {
    console.log('handleAddFiles==', detailData);
    setShowMethodList(true);
    // 调用native的浮层，避免如果有变更，两边同时都要改动
    // openUpdateListBottomSheet();
  }

  function handlePhotoClick() {
    // 跳转APP内拍照生产绘本页面
    openPictureBookQuickInputPage({
      albumId: detailData?.id as number,
      albumTitle: detailData?.name as string,
    })
  }

  function handlePdfClick() {
    // 跳转APP内PDF上传页面
    openPictureBookPdfUploadPage({
      albumId: detailData?.id as number,
      albumTitle: detailData?.name as string,
    })
  }

  function handleBaiduPanClick() {
    // 打开百度网盘页面
    openWebView({
      path: `/self_service_baidu?from=${EFROM.INDEX}&albumId=${detailData?.id}&supportSuffix=pdf`,
      title: '百度网盘上传',
    })
  }

  function handleEditItem(
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    item: IWorksFolder
  ) {
    e.preventDefault();
    e.stopPropagation();
    if (item.type === 0) {
      // 跳转APP内拍照生产绘本页面
      openPictureBookQuickInputPage({
        albumId: detailData?.id as number,
        albumTitle: detailData?.name as string,
        bookId: item?.id,
      })
    } else {
      openWebView({
        path: `/generate_book/create?id=${item.id}`,
        title: '编辑目录',
      })
    }
  }

  function handleOpenSecondFolderOrFile(item: IWorksFolder) {
    if (item.type === 0) {
      // 跳转壳内绘本
      openPictureBookDetailPage(item?.id as number)
    } else {
      // 跳转下一级
      openWebView({
        path: `/generate_book/list?id=${item.id}`,
        title: '自制绘本列表',
      })
    }

  }

  // 删除目录/文件
  function handleDeleteItem(
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    item: IWorksFolder
  ) {
    e.stopPropagation();
    // 删除
    Dialog.show({
      content:
        typeof item.childCnt === 'number' && item.childCnt > 0
          ? `'${item.name}'包含${item.childCnt}个文件，确认删除？`
          : `确认删除'${item.name}'?`,
      closeOnAction: true,
      actions: [
        [
          {
            key: 'cancel',
            text: '取消',
          },
          {
            key: 'delete',
            text: '删除',
            danger: true,
            bold: true,
            onClick: () => {
              if (item.id) {
                Loading.show();
                deleteFolder({ id: item.id })
                  .then((res) => {
                    if (res.success) {
                      Toast.show({
                        content: '已删除',
                      });
                      // 重新获取列表数据
                      if (id) {
                        fetchDirectoryData(Number(id));
                      }
                    } else {
                      Toast.show({
                        content: '删除失败，请稍后重试',
                      });
                    }
                  })
                  .catch((e) => {
                    Toast.show({
                      content: e?.response?.data?.msg || '删除失败，请稍后重试',
                    });
                    console.error('deleteFolder error: ', e);
                  })
                  .finally(() => {
                    Loading.hide();
                  });
              }
            },
          },
        ],
      ],
    });
  }

  function handleOnDragEnd(result: any) {
    console.log('result==', result);
    if (!result.destination) return;
    const obj: number[] = [];
    const itemsCopy = detailData?.items ? Array.from(detailData?.items) : [];
    const [reorderedItem] = itemsCopy.splice(result.source.index, 1);
    itemsCopy.splice(result.destination.index, 0, reorderedItem);

    setDetailData({ ...detailData, ...{ items: itemsCopy } });

    itemsCopy.forEach((cur, index) => {
      if (cur.id) {
        obj.push(cur.id)
      }
    });
    console.log('obj==', obj);
    // TODO 调用排序接口
    moveFolder({
      parentId: detailData?.id,
      ids: obj,
    }).catch((e) => {
      console.error('moveFolder error: ', e);
    })
    
  }

  function handleFinish() {
    console.log('handleFinish==');
    // 提交处理=跳回列表页
    openAlbumList(true)
    // JsBridge.closeWebview(JSON.stringify({ sourceFrom: 'h5' }));
  }

  function closePage() {
    console.log('handleFinish==');
    // 提交处理=跳回列表页
    // openAlbumList(true)
    JsBridge.closeWebview(JSON.stringify({ sourceFrom: 'h5' }));
  }

  useEffect(() => {
    if (id) {
      console.log('id==', id);
      fetchDirectoryData(Number(id));
    }
  }, [id]);

  useEffect(() => {

    JsBridge.setNavigationBar(
      JSON.stringify({ visible: false, statusBarColor: '#FFFFFF' })
    )

    if (window.location.host !== 'sduck.net') {
      const vConsole = new VConsole()
      return () => vConsole.destroy()
    }
    
  }, []);

  useEffect(() => {
    document.title = '自制绘本列表';
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'visibleChange') {
        console.log('visibleChange:', event.data.result);
        if (event.data.result) {
          // 重新获取数据
          if (id) {
            fetchDirectoryData(Number(id));
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, []);

  // 判断是否显示提交按钮
  function shouldShowSubmitBtn(): boolean {
    if (!detailData) return false;
    
    // 如果是type=1且parentType不是2，显示按钮
    if (detailData.type === 1 && detailData.parentType !== 2) {
      return true;
    }
    
    // 如果type不是1，显示按钮
    return detailData.type !== 1;
  }

  function renderHeaderRightBtn() {
    return (
      <button
        onClick={handleFinish}
        className='w-[55px] h-[30px] bg-[#4A90E2] text-white text-[14px] font-medium rounded-[15px] flex items-center justify-center hover:bg-[#357ABD] transition-colors duration-200'
      >
        提交
      </button>
    )
  }

  return (
    <ErrorBoundary fallback={<div>出错了</div>}>
      <div className='flex flex-col h-full'>
        <div className='h-auto'>
          <Header
            title='自制绘本列表'
            isShowBack={true}
            rightBtn={shouldShowSubmitBtn() ? renderHeaderRightBtn() : undefined}
            headerBgColor='#FFFFFF'
            isNeedFixedTop={true}
            titleLeftAlign={true}
            onClickBack={shouldShowSubmitBtn() ? handleFinish : closePage}
          />
          <BaseInfo detailData={detailData} handleOpenEdit={handleOpenEdit} />
          <BtnBox
              handleCreateWorksFolder={handleCreateWorksFolder}
              handleAddFiles={handleAddFiles}
              type={detailData?.type}
              parentType={detailData?.parentType}
              childType={detailData?.items?.length ? detailData.items[0].type : undefined}
              handleFinish={handleFinish}
              closePage={closePage}
          />
        </div>

        {/* 文件或目录列表 */}
        {detailData && detailData?.items?.length === 0 ? (
          <div className="flex flex-col items-center justify-center w-full pt-[180px] pb-[100px]">
            <img src={EmptyBg} alt="empty" className="w-[110px] h-[110px] mb-1" />
            <img src={EmptyTips} alt="empty tips" className="w-[200px] h-[20px]" />
          </div>
        ) : (
          <DragDropContext onDragEnd={handleOnDragEnd}>
            {detailData?.items && (
              <Droppable droppableId='fileOrFolderList'>
                {(provided, snapshot) => (
                  <>
                    <div
                      className='px-[16px] flex-1 overflow-y-auto pb-[100px] min-h-[calc(100vh-191px-80px)]'
                      ref={provided.innerRef}
                      // isDraggingFrom={Boolean(snapshot.draggingFromThisWith)}
                      {...provided.droppableProps}
                    >
                      {detailData?.items &&
                        detailData?.items.map((item, index) => {
                          return (
                            <Draggable
                              key={item.id}
                              draggableId={item.id + ''}
                              index={index}
                              // disableInteractiveElementBlocking={false}
                            >
                              {(provided) => (
                                <div
                                  onClick={() =>
                                    handleOpenSecondFolderOrFile(item)
                                  }
                                  className='w-[100%] min-h-[82px] bg-[#EEF9FA] rounded-[15px] p-[12px] relative mt-[12px]'
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  // {...provided.dragHandleProps}
                                >
                                  {/* 内容展示 */}
                                  <div className='flex'>
                                    <img
                                      className='w-[54px] h-[54px] mr-[12px] rounded-[8px]'
                                      src={FolderImage(item.imageUrl)}
                                      alt=''
                                    />
                                    <div className='flex flex-col justify-around min-h-[54px] w-[calc(100%_-_100px)]'>
                                      <div className='text-[16px] flex-1 break-all'>
                                        {item.name}
                                      </div>

                                      <div className='mt-[4px] text-[12px] text-[#333333]'>
                                        更新时间：
                                        {item.updateTime?.replace('T', ' ')}
                                      </div>
                                    </div>
                                  </div>
                                  {/* 操作按钮 */}
                                  <div className='flex items-center mt-[12px]'>
                                      <div
                                        onClick={(e) =>
                                          handleEditItem(e, item)
                                        }
                                        className='border border-[#C0C0C0] rounded-[4px] text-[#505050] w-[50px] h-[24px] flex justify-center items-center mr-[16px]'
                                      >
                                        编辑
                                      </div>
                                      <div
                                        onClick={(e) =>
                                          handleDeleteItem(e, item)
                                        }
                                        className='border border-[#C0C0C0] rounded-[4px] text-[#505050] w-[50px] h-[24px] flex justify-center items-center'
                                      >
                                        删除
                                      </div>
                                    </div>
                                    <div
                                      className='flex justify-center items-center absolute w-[60px] h-[100%] top-[50%] right-0 transform translate-y-[-50%]'
                                      {...provided.dragHandleProps}
                                    >
                                      <img
                                        className='w-[24px] h-[24px]'
                                        src={TouchMoveIcon}
                                        alt=''
                                      />
                                    </div>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                    </div>
                    <div className='absolute left-[10000px]'>
                      {provided.placeholder}
                    </div>
                  </>
                )}
              </Droppable>
            )}
          </DragDropContext>
        )}
        <AddResourceMethodList 
          visible={showMethodList}
          onClose={() => setShowMethodList(false)}
          onPhotoClick={handlePhotoClick}
          onPdfClick={handlePdfClick}
          onBaiduPanClick={handleBaiduPanClick}
          showBaiduPan={shouldShowBaiduPan()}
        />
        <CreateFolderMethodList
          visible={showCreateMethodList}
          onClose={() => setShowCreateMethodList(false)}
          onManualCreate={handleManualCreateFolder}
          onBaiduPanImport={handleBaiduPanImportFolder}
          showBaiduPan={shouldShowBaiduPan()}
        />
      </div>
    </ErrorBoundary>
  );
}

export default ResourcesList;
